import React, { useRef, useState, DragEvent } from "react";
import { tss } from "tss-react";
import GenericInputWrapper from "./GenericInputWrapper";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import { FileType, FILE_TYPES_META } from "src/models/file";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTheme } from "@mui/material/styles";

interface IGenericFileInputProps {
    title: string;
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    error?: string;
    name: string;
    disabled?: boolean;
    placeholder?: string;
    helperText?: string;
    className?: string;
    hideError?: boolean;
    containerClassName?: string;
    allowedTypes: FileType[];
    maxSizeInMbs?: number;
    inputRef?: (ref: any) => void;
    multiple?: boolean;
}

const useStyles = tss
    .withParams<{ isDragging: boolean; hasFile: boolean; hasError: boolean; disabled: boolean; theme: any }>()
    .create(({ isDragging, hasFile, hasError, disabled, theme }) => ({
        uploadContainer: {
            width: "100%",
            position: "relative"
        },
        uploadBox: {
            border: `2px dashed ${
                hasError 
                    ? theme.palette.error.main 
                    : isDragging 
                    ? theme.palette.primary.main 
                    : hasFile
                    ? theme.palette.success.main
                    : theme.palette.divider
            }`,
            borderRadius: theme.shape.borderRadius * 2,
            padding: theme.spacing(3),
            textAlign: "center",
            cursor: disabled ? "not-allowed" : "pointer",
            transition: "all 0.3s ease",
            backgroundColor: isDragging 
                ? theme.palette.action.hover 
                : hasFile
                ? theme.palette.success.light + '10'
                : theme.palette.background.paper,
            opacity: disabled ? 0.6 : 1,
            "&:hover": {
                backgroundColor: disabled 
                    ? theme.palette.background.paper 
                    : theme.palette.action.hover,
                borderColor: disabled 
                    ? theme.palette.divider 
                    : hasError
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                transform: disabled ? "none" : "translateY(-2px)",
                boxShadow: disabled ? "none" : theme.shadows[2]
            }
        },
        uploadIcon: {
            fontSize: 48,
            color: hasFile 
                ? theme.palette.success.main 
                : theme.palette.action.active,
            marginBottom: theme.spacing(1),
            transition: "transform 0.3s ease",
            "&:hover": {
                transform: disabled ? "none" : "scale(1.1)"
            }
        },
        uploadText: {
            marginBottom: theme.spacing(1),
            fontWeight: 500,
            color: theme.palette.text.primary
        },
        uploadHint: {
            fontSize: "0.875rem",
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(0.5)
        },
        fileInfo: {
            marginTop: theme.spacing(2),
            padding: theme.spacing(1.5),
            backgroundColor: "#fff",
            borderRadius: theme.shape.borderRadius,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: theme.spacing(1)
        },
        fileDetails: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            flex: 1,
            minWidth: 0
        },
        fileName: {
            fontWeight: 500,
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
        },
        fileSize: {
            fontSize: "0.75rem",
            color: theme.palette.text.secondary
        },
        removeButton: {
            padding: theme.spacing(0.5)
        },
        helperText: {
            color: theme.palette.text.secondary,
            fontSize: "0.75rem",
            marginTop: theme.spacing(1),
            textAlign: "left"
        },
        hiddenInput: {
            display: "none"
        },
        label: {
            fontSize: "0.875rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
            display: "block"
        },
        successChip: {
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.dark,
            height: 24,
            "& .MuiChip-icon": {
                fontSize: 16
            }
        }
    }));

const GenericFileInput: React.FC<IGenericFileInputProps> = ({
    title,
    value,
    disabled,
    error,
    onChange,
    onBlur,
    onKeyDown,
    name,
    placeholder,
    helperText,
    className,
    hideError,
    containerClassName,
    allowedTypes,
    maxSizeInMbs,
    inputRef,
    multiple = false
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const hasFile = multiple
        ? (Array.isArray(value) && value.length > 0)
        : (!!value && (typeof value === 'string' || value instanceof File));

    const theme = useTheme();
    const { classes, cx } = useStyles({
        isDragging,
        hasFile,
        hasError: !!error,
        disabled: !!disabled,
        theme
    });

    // Set up ref
    React.useEffect(() => {
        if (inputRef) {
            inputRef(fileInputRef.current);
        }
    }, [inputRef]);

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        if (!disabled) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        if (disabled) return;

        if (multiple) {
            const files = Array.from(e.dataTransfer.files);
            const validFiles = files.filter(file => validateFile(file));
            if (validFiles.length > 0) {
                onChange(validFiles);
            }
        } else {
            const file = e.dataTransfer.files[0];
            if (file && validateFile(file)) {
                onChange(file);
            }
        }
    };

    const handleClick = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (multiple) {
            const files = e.target.files ? Array.from(e.target.files) : [];
            const validFiles = files.filter(file => validateFile(file));
            if (validFiles.length > 0) {
                onChange(validFiles);
            }
        } else {
            const file = e.target.files?.[0];
            if (file && validateFile(file)) {
                onChange(file);
            }
        }
    };

    const validateFile = (file: File): boolean => {
        // Check file size
        if (maxSizeInMbs && file.size > maxSizeInMbs * 1024 * 1024) {
            // You might want to show an error here
            return false;
        }
        return true;
    };

    const handleRemove = (e: React.MouseEvent, index?: number) => {
        e.stopPropagation();
        if (multiple && Array.isArray(value) && index !== undefined) {
            const newFiles = value.filter((_, i) => i !== index);
            onChange(newFiles.length > 0 ? newFiles : []);
        } else {
            onChange(multiple ? [] : '');
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileName = (file?: any): string => {
        const targetFile = file || value;
        if (typeof targetFile === 'string') return targetFile;
        if (targetFile instanceof File) return targetFile.name;
        return '';
    };

    const getFileSize = (file?: any): number => {
        const targetFile = file || value;
        if (targetFile instanceof File) return targetFile.size;
        return 0;
    };

    return (
        <GenericInputWrapper 
            customClasses={{ main: containerClassName }} 
            hideError={hideError} 
            title="" 
            error={error}  // Keep this - GenericInputWrapper will handle error display
            name={name}
        >
            <Box className={cx(classes.uploadContainer, className)}>
                {title && (
                    <Typography className={classes.label}>
                        {title}
                    </Typography>
                )}
                
                <Box
                    className={classes.uploadBox}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleClick}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleClick();
                        }
                        onKeyDown?.(e);
                    }}
                    tabIndex={disabled ? -1 : 0}
                    role="button"
                    aria-label="Upload file"
                    onBlur={onBlur}
                >
                    {hasFile ? (
                        <CheckCircleIcon className={classes.uploadIcon} />
                    ) : (
                        <CloudUploadIcon className={classes.uploadIcon} />
                    )}
                    
                    <Typography className={classes.uploadText}>
                        {hasFile
                            ? (multiple && Array.isArray(value)
                                ? `${value.length} file(s) uploaded successfully`
                                : "File uploaded successfully")
                            : placeholder || (multiple
                                ? "Drag and drop files here or click to browse"
                                : "Drag and drop a file here or click to browse")
                        }
                    </Typography>

                    {!hasFile && (
                        <Typography className={classes.uploadHint}>
                            {allowedTypes.length > 0
                                ? `Accepted formats: ${allowedTypes.map(type => FILE_TYPES_META[type]?.extension || type).join(', ')}`
                                : "All file types accepted"
                            }
                            {maxSizeInMbs && ` • Max size: ${maxSizeInMbs}MB`}
                            {multiple && " • Multiple files allowed"}
                        </Typography>
                    )}

                    {hasFile && !multiple && (
                        <Box className={classes.fileInfo} onClick={(e) => e.stopPropagation()}>
                            <Box className={classes.fileDetails}>
                                <InsertDriveFileIcon fontSize="small" color="primary" />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography className={classes.fileName}>
                                        {getFileName()}
                                    </Typography>
                                    {getFileSize() > 0 && (
                                        <Typography className={classes.fileSize}>
                                            {formatFileSize(getFileSize())}
                                        </Typography>
                                    )}
                                </Box>
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label="Uploaded"
                                    size="small"
                                    className={classes.successChip}
                                />
                            </Box>
                            <IconButton
                                className={classes.removeButton}
                                onClick={(e) => handleRemove(e)}
                                size="small"
                                disabled={disabled}
                                aria-label="Remove file"
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}

                    {hasFile && multiple && Array.isArray(value) && (
                        <Box sx={{ mt: 2 }}>
                            {value.map((file: File, index: number) => (
                                <Box key={index} className={classes.fileInfo} onClick={(e) => e.stopPropagation()} sx={{ mb: 1 }}>
                                    <Box className={classes.fileDetails}>
                                        <InsertDriveFileIcon fontSize="small" color="primary" />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography className={classes.fileName}>
                                                {getFileName(file)}
                                            </Typography>
                                            {getFileSize(file) > 0 && (
                                                <Typography className={classes.fileSize}>
                                                    {formatFileSize(getFileSize(file))}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Chip
                                            icon={<CheckCircleIcon />}
                                            label="Uploaded"
                                            size="small"
                                            className={classes.successChip}
                                        />
                                    </Box>
                                    <IconButton
                                        className={classes.removeButton}
                                        onClick={(e) => handleRemove(e, index)}
                                        size="small"
                                        disabled={disabled}
                                        aria-label="Remove file"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                
                {/* REMOVED: Custom error display - let GenericInputWrapper handle it */}
                
                {/* Only show helperText when there's no error */}
                {helperText && !error && (
                    <Typography className={classes.helperText}>
                        {helperText}
                    </Typography>
                )}
                
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept={allowedTypes.map(type => FILE_TYPES_META[type]?.mimetype || '*/*').join(', ')}
                    className={classes.hiddenInput}
                    disabled={disabled}
                    multiple={multiple}
                />
            </Box>
        </GenericInputWrapper>
    );
};

export default GenericFileInput;